import { useEffect } from 'react';
import { DimensionType, DimensionValueGroupingType } from '@sleed/bizcommerce-api';
import { Controller, FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import type { DimensionValueGroupingToken, DimensionValueToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '~/components/system/form-field';
import { ImageSlotField } from '~/components/image-slot-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useDimensionValueMutations, uploadDimensionValueImage, dimensionValueKeys } from '~/hooks/use-dimension-values';
import { useDimensionGroupingsList } from '~/hooks/use-dimension-groupings';
import { useDimensionsList } from '~/hooks/use-dimensions';
import { dimensionValueSchema, emptyDimensionValue, tokenToForm, type DimensionValueFormValues } from '~/lib/schemas/dimension-value';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	did: number;
	initial: DimensionValueToken | null;
}

const NO_GROUP = '__none__';

/**
 * The groupings a value of this dimension may join (mirrors DimensionValuesController.IsGroupingTypeAllowedAsync): colour ->
 * Color (3); size -> Footwear (1), Clothing (2) or Size (4); a Custom value -> a grouping of its own dimension (BW-8412).
 */
function canJoinGrouping(dimensionType: number | undefined, did: number, g: DimensionValueGroupingToken) {
	const type = g.dgt as number | undefined;
	if (dimensionType === DimensionType.Color) return type === DimensionValueGroupingType.Color;
	if (dimensionType === DimensionType.Size)
		return type === DimensionValueGroupingType.Footwear || type === DimensionValueGroupingType.Clothing || type === DimensionValueGroupingType.Size;
	return g.did === did;
}

/** Color picker bound to a nullable RHF hex field. */
function ColorField({ name, label }: { name: 'chex'; label: string }) {
	const { control, setValue } = useFormContext<DimensionValueFormValues>();
	const value = useWatch({ control, name });
	return (
		<FormField label={label}>
			<div className="flex items-center gap-2">
				<input
					type="color"
					value={value || '#000000'}
					onChange={e => setValue(name, e.target.value, { shouldDirty: true })}
					className="h-9 w-12 cursor-pointer rounded-md border border-border bg-surface p-1"
					aria-label={label}
				/>
				<Input value={value ?? ''} onChange={e => setValue(name, e.target.value || null, { shouldDirty: true })} placeholder="#RRGGBB" />
			</div>
		</FormField>
	);
}

/** Add / edit a dimension value (Τιμή διάστασης). Wrapped in FormProvider so the image slot can bind. */
export function DimensionValueEditDialog({ open, onOpenChange, did, initial }: Props) {
	const { t } = useTranslation();
	const qc = useQueryClient();
	const { create, update } = useDimensionValueMutations(did);
	const { data: groupings } = useDimensionGroupingsList();
	const { data: dimensions } = useDimensionsList();
	const dimensionType = (dimensions ?? []).find(d => d.id === did)?.dt as number | undefined;
	const form = useForm<DimensionValueFormValues>({
		resolver: zodResolver(dimensionValueSchema) as Resolver<DimensionValueFormValues>,
		defaultValues: emptyDimensionValue(did),
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, control, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ? tokenToForm(initial) : emptyDimensionValue(did));
	}, [open, initial, did, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			const saved = values.id ? await update.mutateAsync(values) : await create.mutateAsync(values);
			if (saved.id && values.img1File instanceof File) {
				await uploadDimensionValueImage(saved.id, '1', values.img1File);
				await qc.invalidateQueries({ queryKey: dimensionValueKeys.byDimension(did) });
			}
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-xl">
				<DialogHeader>
					<DialogTitle>{isEdit ? t('attributes.dimensionValues.editTitle') : t('attributes.dimensionValues.addTitle')}</DialogTitle>
					<DialogDescription>{t('attributes.dimensionValues.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<FormProvider {...form}>
					<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
						<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
							<FormField label={t('attributes.dimensionValues.columns.value')} error={formState.errors.vtxt?.message}>
								<Input {...register('vtxt')} aria-invalid={Boolean(formState.errors.vtxt)} autoFocus />
							</FormField>
							<FormField label={t('attributes.dimensionValues.columns.code')} error={formState.errors.cd?.message}>
								<Input {...register('cd')} />
							</FormField>
						</div>
						<FormField label={t('attributes.dimensionValues.columns.erpCode')} error={formState.errors.erpcd?.message}>
							<Input {...register('erpcd')} maxLength={50} aria-invalid={Boolean(formState.errors.erpcd)} />
						</FormField>
						<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
							<ColorField name="chex" label={t('attributes.dimensionValues.columns.color')} />
							<FormField label={t('attributes.dimensionValues.columns.grouping')}>
								<Controller
									control={control}
									name="dvg"
									render={({ field }) => (
										<Select
											value={field.value != null ? String(field.value) : NO_GROUP}
											onValueChange={v => field.onChange(v === NO_GROUP ? null : Number(v))}>
											<SelectTrigger>
												<SelectValue placeholder={t('attributes.dimensionValues.noGrouping')} />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value={NO_GROUP}>{t('attributes.dimensionValues.noGrouping')}</SelectItem>
												{(groupings ?? [])
													.filter(g => g.id === watch('dvg') || canJoinGrouping(dimensionType, did, g))
													.map(g => (
													<SelectItem key={g.id} value={String(g.id)}>
														{g.nm}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									)}
								/>
							</FormField>
						</div>
						<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
							<FormField label={t('attributes.dimensionValues.columns.altValue1')}>
								<Input {...register('vtxt_a1')} />
							</FormField>
							<FormField label={t('attributes.dimensionValues.columns.altValue2')}>
								<Input {...register('vtxt_a2')} />
							</FormField>
						</div>
						<ImageSlotField
							urlField="img1"
							fileField="img1File"
							delField="imgdel1"
							label={t('attributes.dimensionValues.columns.image')}
							uploadLabel={t('attributes.dimensionValues.uploadImage')}
							replaceLabel={t('attributes.dimensionValues.replaceImage')}
						/>
						<div className="grid grid-cols-2 gap-3">
							<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
								<Label htmlFor="dv-iv">{t('attributes.dimensionValues.columns.visible')}</Label>
								<Switch id="dv-iv" checked={watch('iv')} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
							</div>
							<FormField label={t('attributes.dimensionValues.columns.sort')} error={formState.errors.si?.message}>
								<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
							</FormField>
						</div>
						<DialogFooter>
							<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
								{t('common.cancel')}
							</Button>
							<Button type="submit" disabled={create.isPending || update.isPending}>
								{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
							</Button>
						</DialogFooter>
					</form>
				</FormProvider>
			</DialogContent>
		</Dialog>
	);
}
